import { useEffect, useRef, useState } from "react";
import { cjsDefault } from "../../lib/cjs-default";
import { useApiQuery } from "../../services/api";
import reactFadeIn from "react-fade-in/lib/FadeIn";
import { useLocation } from "react-router";
import { useHistory } from "react-router-dom";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { gaEventSelectItemPlaylist } from "../../tools/analytics/domains/playlist.analytics";
import ContentHeader from "../../components/app/headers/ContentHeader";
import { NavLang } from "../../components/app/headers/layout";
import ItemList from "../../components/app/ItemList";
import Navbar from "../../components/app/navbars/Navbar";
import PlaylistCard from "../../components/cards/PlaylistCard";
import SpecialtyFilterPanel from "../../components/filters/SpecialtyFilterPanel";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { usePageScroll } from "../../domains/app/hooks/usePageScroll";
import { getStoredContentFilters } from "../../domains/app/utils/contentFilters";
import { playlistScrollStore } from "../../domains/playlist/stores/playlistScrollStore";
import { useGetCertifiedPlaylistsQuery } from "../../domains/playlist/hooks/useGetCertifiedPlaylistsQuery";
import { useGetCompanyPlaylistsQuery } from "../../domains/playlist/hooks/useGetCompanyPlaylistsQuery";
import {
  useGetRecommendedPlaylistsQuery,
  useGetPersonalRecommendedPlaylistsQuery,
} from "../../domains/playlist/hooks/useGetRecommendedPlaylistsQuery";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventViewItemListPlaylist } from "../../tools/analytics/domains/playlist.analytics";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useTranslation } from "react-i18next";

const FadeIn = cjsDefault(reactFadeIn);

export default function PlaylistsListPage({
  roomId,
  companyId,
}: {
  roomId: string | null;
  companyId?: string;
}) {
  const location = useLocation();
  const history = useHistory();
  const { publicPlaylistLink } = usePageLinks(roomId);
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    roomId !== null ? { param: { id: roomId } } : false
  );
  const isInRoom = roomId !== null;
  const scrollRef = useRef<HTMLDivElement>(null);
  const searchParams = new URLSearchParams(location.search);
  const isCertified = searchParams.get("kol") === "true";
  const { activeLang } = useActiveLang();
  const { t } = useTranslation();
  const [specialtyFilters, setSpecialtyFilters] = useState<string[]>(
    getStoredContentFilters().specialtyIds
  );
  // `/discovery/playlists` is the "See all" of the Discovery collections rail,
  // so it must rank the same way the rail does: MeSH overlap with the caller's
  // profile, via the private endpoint. The room list keeps the public endpoint
  // (a room's collections are editorially ordered, not recommended), as do the
  // company and KOL lists which are explicit curated corpora.
  const isPersonalList =
    roomId === null && companyId === undefined && !isCertified;
  const personalCache = useGetPersonalRecommendedPlaylistsQuery(null);
  const standardCache = useGetRecommendedPlaylistsQuery(roomId, {
    medicalSpecialties: specialtyFilters.join(","),
  });
  const certifiedCache = useGetCertifiedPlaylistsQuery(roomId, {
    medicalSpecialties: specialtyFilters.join(","),
  });
  const organizationCache = useGetCompanyPlaylistsQuery({
    companyId: companyId ?? "",
  });
  const activeCache = companyId
    ? organizationCache
    : isCertified
      ? certifiedCache
      : isPersonalList
        ? personalCache
        : standardCache;
  const displayedPlaylists = activeCache.playlists;
  const displayedPlaylistIds = activeCache.playlistIds;
  const isRoomLoading = roomQuery.state === "pending" && roomQuery.isFetching;
  const isLoading = isInRoom
    ? isRoomLoading || activeCache.isLoading
    : activeCache.isLoading;
  const hadCachedPlaylists = useRef(!isLoading);
  const { savePageScrollY, pageScrollY } = usePageScroll();
  const [isFilterPanelVisible, setFilterPanelVisible] = useState(false);

  // Restore scroll position when navigating back, but only for the same room
  useEffect(() => {
    const savedScrollRoomId = playlistScrollStore.get().lastListRoomId;

    if (isInRoom === isInRoom && !isLoading && scrollRef.current) {
      const isSameRoom = !roomId || savedScrollRoomId === roomId;

      if (isSameRoom && pageScrollY > 0 && displayedPlaylists.length > 0) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollRef.current?.scrollTo({
              top: pageScrollY,
              behavior: "instant",
            });
          });
        });
      } else if (roomId && savedScrollRoomId !== roomId) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollRef.current?.scrollTo({ top: 0, behavior: "instant" });
            savePageScrollY(0);
          });
        });
      }
    }
  }, [
    isInRoom,
    isInRoom,
    activeLang,
    specialtyFilters,
    isLoading,
    location.key,
    roomId,
  ]);

  useEffectOnce(() => {
    if (displayedPlaylists.length > 0) {
      gaEventViewItemListPlaylist({
        playlists: displayedPlaylists,
        listName: AnalyticsItemList.HOME,
      });
    }
  }, [displayedPlaylistIds]);

  function handleChangeFilters() {
    setSpecialtyFilters(getStoredContentFilters().specialtyIds);
  }

  function handleScrollEnd() {
    if (activeCache.canFetchNextPage) activeCache.fetchNextPage();
  }

  return (
    <ScrollableLayout
      scrollRef={scrollRef}
      headerClassName="bg-white/90 backdrop-blur-md"
      header={
        <ContentHeader
          transparent
          title={t("Playlists")}
          actions={<NavLang />}
        />
      }
      footer={isInRoom ? null : <Navbar />}
      onScrollEnd={handleScrollEnd}
      background="var(--color-ds-white)"
    >
      <div className="pt-4">
        {(() => {
          const variantProps =
            roomQuery.state === "success"
              ? ({
                  variant: "in-room",
                  primaryColor: roomQuery.body.primaryColor,
                  secondaryColor: roomQuery.body.secondaryColor,
                } as const)
              : ({ variant: "light" } as const);
          if (isLoading)
            return (
              <FadeIn wrapperTag={ItemList} delay={60}>
                {Array.from({ length: 10 }, (_, i) => (
                  <PlaylistCard key={i} loading {...variantProps} />
                ))}
              </FadeIn>
            );
          if (displayedPlaylists.length > 0) {
            const cards = displayedPlaylists.map((playlist) => (
              <PlaylistCard
                key={playlist.id + "pagecontentkey"}
                playlist={playlist}
                {...variantProps}
                onClick={() => {
                  savePageScrollY(scrollRef.current?.scrollTop ?? 0);
                  if (roomId !== null) {
                    playlistScrollStore.set((prev) => ({
                      ...prev,
                      lastListRoomId: roomId,
                    }));
                  }
                  gaEventSelectItemPlaylist({
                    playlist,
                    listName: AnalyticsItemList.HOME,
                  });
                  history.push({
                    pathname: publicPlaylistLink(playlist),
                    state: { playlist },
                  });
                }}
              />
            ));
            return hadCachedPlaylists.current ? (
              <ItemList>{cards}</ItemList>
            ) : (
              <FadeIn wrapperTag={ItemList} delay={60}>
                {cards}
              </FadeIn>
            );
          }
          return (
            <div className="px-[22px] py-[8px] text-[16px] font-bold text-primary-black leading-[110%]">
              {t("No content")}
            </div>
          );
        })()}
      </div>

      <SpecialtyFilterPanel
        room={null}
        isOpen={isFilterPanelVisible}
        onClose={() => {
          setFilterPanelVisible(false);
        }}
        onSave={() => {
          handleChangeFilters();
        }}
      />
    </ScrollableLayout>
  );
}
